import { useEffect, useState } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { Heart, Trash2, Search, BookmarkX, MapPin, GraduationCap, BookOpen } from 'lucide-react';
import { StudentLayout } from '@/components/student/layout/StudentLayout';
import { Button } from '@/components/ui/button';
import { useWishlist } from '@/hooks/useWishlist';
import { getTeacherByHandle, type PublicTeacherProfile } from '@/services/marketplaceService';
import { SubscriptionTierBadge } from '@/components/teacher/SubscriptionTierBadge';
import { cn } from '@/lib/utils';

// ── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'from-violet-500 to-indigo-600',
  'from-sky-500 to-amber-600',
  'from-emerald-500 to-amber-600',
  'from-orange-500 to-rose-500',
  'from-pink-500 to-fuchsia-600',
];

function getGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// ── Date helper ──────────────────────────────────────────────────────────────

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return 'Saved today';
  if (days === 1) return 'Saved yesterday';
  if (days < 7) return `Saved ${days} days ago`;
  if (days < 30) return `Saved ${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
  return `Saved on ${new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

// ── Subject colour chips ─────────────────────────────────────────────────────

const SUBJECT_COLORS = [
  { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-400' },
  { bg: 'bg-sky-50',    text: 'text-sky-700',    dot: 'bg-sky-400' },
  { bg: 'bg-emerald-50',text: 'text-emerald-700',dot: 'bg-emerald-400' },
  { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-400' },
  { bg: 'bg-rose-50',   text: 'text-rose-700',   dot: 'bg-rose-400' },
];

// ── Skeleton ─────────────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-pulse">
      <div className="h-36 bg-slate-100" />
      <div className="p-4 space-y-2.5">
        <div className="h-4 bg-slate-100 rounded w-3/5" />
        <div className="h-3 bg-slate-100 rounded w-2/5" />
        <div className="h-3 bg-slate-100 rounded w-4/5" />
        <div className="h-8 bg-slate-100 rounded-lg mt-3" />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SavedTeachersPage() {
  usePageTitle('Saved Teachers | BookMyCoaching');
  const { items, loading: wishlistLoading, remove } = useWishlist();
  const [profiles, setProfiles] = useState<Record<string, PublicTeacherProfile>>({});
  const [profilesLoading, setProfilesLoading] = useState(false);

  // Once the wishlist has resolved and has items, fetch all teacher profiles in parallel
  useEffect(() => {
    if (wishlistLoading || items.length === 0) return;

    let cancelled = false;
    setProfilesLoading(true);

    void Promise.allSettled(items.map((item) => getTeacherByHandle(item.teacherHandle))).then(
      (results) => {
        if (cancelled) return;
        const map: Record<string, PublicTeacherProfile> = {};
        results.forEach((result, i) => {
          if (result.status === 'fulfilled') map[items[i].teacherHandle] = result.value;
        });
        setProfiles(map);
        setProfilesLoading(false);
      },
    );

    return () => { cancelled = true; };
  }, [wishlistLoading, items]);

  const loading = wishlistLoading || profilesLoading;

  return (
    <StudentLayout>
      <main className="flex-1 min-h-screen bg-slate-50">
        <div className="px-4 sm:px-6 lg:px-8 py-8">

          {/* Page header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Saved Teachers</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {loading
                  ? 'Loading your saved teachers…'
                  : items.length === 0
                    ? 'No teachers saved yet'
                    : `${items.length} teacher${items.length > 1 ? 's' : ''} on your list`}
              </p>
            </div>
            {!loading && items.length > 0 && (
              <Button asChild variant="outline" size="sm">
                <Link to="/search">
                  <Search className="w-3.5 h-3.5 mr-1.5" />
                  Explore more
                </Link>
              </Button>
            )}
          </div>

          {/* Empty state — only shown once loading is fully done */}
          {!loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-4">
                <BookmarkX className="w-8 h-8 text-rose-300" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Your list is empty</h2>
              <p className="text-sm text-slate-500 max-w-xs mb-6">
                Browse teachers and tap the heart icon to save ones you'd like to revisit.
              </p>
              <Button asChild>
                <Link to="/search">
                  <Search className="w-4 h-4 mr-1.5" />
                  Find teachers
                </Link>
              </Button>
            </div>
          )}

          {/* Skeleton grid while profiles load */}
          {items.length > 0 && loading && (
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map((item) => <CardSkeleton key={item.id} />)}
            </div>
          )}

          {/* Teacher cards */}
          {items.length > 0 && !loading && (
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map((item) => {
                const profile = profiles[item.teacherHandle];
                const gradient = getGradient(item.teacherName);
                const subjects = profile?.subjects?.slice(0, 3) ?? [];
                const extraSubjects = (profile?.subjects?.length ?? 0) - 3;
                const bio = profile?.professionalBio?.trim();
                const shortBio = bio && bio.length > 100 ? bio.slice(0, 100).trimEnd() + '…' : bio;

                return (
                  <div
                    key={item.id}
                    className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
                  >
                    {/* Photo / gradient banner */}
                    <div className="relative h-36 overflow-hidden flex-shrink-0">
                      {profile?.profileImage ? (
                        <img
                          src={profile.profileImage}
                          alt={item.teacherName}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className={cn('w-full h-full bg-gradient-to-br flex items-center justify-center', gradient)}>
                          <span className="text-white font-bold text-5xl">{getInitials(item.teacherName)}</span>
                        </div>
                      )}
                      {/* Gradient scrim so text is always readable */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                      {/* Name + tier overlaid on photo */}
                      <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
                        <div className="flex items-end justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="font-extrabold text-white text-base leading-tight truncate drop-shadow-md">
                              {item.teacherName}
                            </h3>
                            <p className="text-xs text-white/70 mt-0.5">@{item.teacherHandle}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {profile?.teacherTier && (
                              <SubscriptionTierBadge tier={profile.teacherTier} />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Remove button — top right */}
                      <button
                        onClick={() => remove(item.teacherHandle)}
                        className="absolute top-2.5 right-2.5 p-1.5 rounded-full bg-black/30 backdrop-blur-sm text-white/80 hover:bg-red-500 hover:text-white transition-colors"
                        aria-label="Remove from saved"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Card body */}
                    <div className="flex flex-col flex-1 p-4 gap-3">

                      {/* Bio snippet */}
                      {shortBio && (
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{shortBio}</p>
                      )}

                      {/* Subject chips */}
                      {subjects.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {subjects.map((s, i) => {
                            const c = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
                            return (
                              <span
                                key={s.subjectId}
                                className={cn(
                                  'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
                                  c.bg, c.text,
                                )}
                              >
                                <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', c.dot)} />
                                {s.subjectName}
                              </span>
                            );
                          })}
                          {extraSubjects > 0 && (
                            <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              +{extraSubjects}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Meta row: location + experience */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {profile?.locationDisplayName && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            {profile.locationDisplayName}
                          </span>
                        )}
                        {profile?.yearsTeachingExperience && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <GraduationCap className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            {profile.yearsTeachingExperience} yrs exp
                          </span>
                        )}
                        {profile?.highestQualification && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <BookOpen className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            {profile.highestQualification}
                          </span>
                        )}
                      </div>

                      {/* Saved date */}
                      <p className="flex items-center gap-1 text-xs text-slate-400">
                        <Heart className="w-3 h-3 text-rose-400 fill-rose-400 flex-shrink-0" />
                        {relativeDate(item.savedAt)}
                      </p>

                      {/* CTA */}
                      <div className="mt-auto pt-1">
                        <Button asChild className="w-full" size="sm">
                          <Link to={`/t/${item.teacherHandle}`}>View Profile</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </StudentLayout>
  );
}
